import { useRef, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router';
import { MapContainer, TileLayer, Circle, useMap, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import type { Map as LeafletMap } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.css';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.Default.css';
import { useGeolocationContext } from '../../contexts/GeolocationContext.js';
import { useMapSpots } from './useMapSpots.js';
import { SpotMarker } from './SpotMarker.js';
import { CurrentLocationMarker } from './CurrentLocationMarker.js';
import { WeatherBadge } from './WeatherBadge.js';
import { AddressSearchBox } from './AddressSearchBox.js';
import { clearWeatherCache } from '../../hooks/useWeather.js';
import type { Spot } from '@ridehub/shared';
import { getCategoryLabel, calculateStarScore } from '@ridehub/shared';
import type { SpotCategory } from '@ridehub/shared';

function RegisterSpotIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

const DEFAULT_LAT = 35.6762;
const DEFAULT_LNG = 139.6503;
const DEFAULT_ZOOM = 16;
const MAX_SEARCH_RADIUS = 10000; // 10km
const MAP_STATE_KEY = 'ridehub_map_state';

interface MapBounds {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}

interface SavedMapState {
  lat: number;
  lng: number;
  zoom: number;
  bbox?: MapBounds;
}

function loadMapState(): SavedMapState | null {
  try {
    const saved = sessionStorage.getItem(MAP_STATE_KEY);
    return saved ? (JSON.parse(saved) as SavedMapState) : null;
  } catch {
    return null;
  }
}

function saveMapState(state: SavedMapState) {
  try {
    sessionStorage.setItem(MAP_STATE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage 利用不可の場合は無視
  }
}

// 表示範囲を 10km でキャップした bbox を返す
function capBounds(map: LeafletMap): MapBounds {
  const center = map.getCenter();
  const raw = map.getBounds();
  const latDelta = MAX_SEARCH_RADIUS / 111320;
  const lngDelta = MAX_SEARCH_RADIUS / (111320 * Math.cos((center.lat * Math.PI) / 180));
  return {
    minLat: Math.max(raw.getSouth(), center.lat - latDelta),
    maxLat: Math.min(raw.getNorth(), center.lat + latDelta),
    minLng: Math.max(raw.getWest(), center.lng - lngDelta),
    maxLng: Math.min(raw.getEast(), center.lng + lngDelta),
  };
}

// マップマウント時・ビューポート変化時にサイズを再計算して描画ズレを防ぐ
function MapInvalidator() {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize();
    const onResize = () => map.invalidateSize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [map]);
  return null;
}

// マウント直後に表示範囲から初期 bounds を算出して通知
function MapInitHandler({
  onInit,
  onActualBoundsChange,
}: {
  onInit: (bounds: MapBounds) => void;
  onActualBoundsChange: (bounds: MapBounds) => void;
}) {
  const map = useMap();
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;
    const bounds = capBounds(map);
    const raw = map.getBounds();
    const center = map.getCenter();
    saveMapState({ lat: center.lat, lng: center.lng, zoom: map.getZoom(), bbox: bounds });
    onInit(bounds);
    onActualBoundsChange({
      minLat: raw.getSouth(),
      maxLat: raw.getNorth(),
      minLng: raw.getWest(),
      maxLng: raw.getEast(),
    });
  }, [map, onInit, onActualBoundsChange]);

  return null;
}

// 移動・ズームに応じて bounds を通知し sessionStorage に保存
function MapEventHandler({
  onMoveEnd,
  onActualBoundsChange,
}: {
  onMoveEnd: (bounds: MapBounds) => void;
  onActualBoundsChange: (bounds: MapBounds) => void;
}) {
  const map = useMap();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const update = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const center = map.getCenter();
      const bounds = capBounds(map);
      const raw = map.getBounds();
      saveMapState({ lat: center.lat, lng: center.lng, zoom: map.getZoom(), bbox: bounds });
      onMoveEnd(bounds);
      onActualBoundsChange({
        minLat: raw.getSouth(),
        maxLat: raw.getNorth(),
        minLng: raw.getWest(),
        maxLng: raw.getEast(),
      });
    }, 300);
  }, [map, onMoveEnd, onActualBoundsChange]);

  useMapEvents({ moveend: update, zoomend: update });
  return null;
}

// マップのクリック/タップで選択スポットを閉じる
function MapClickHandler({ onClose }: { onClose: () => void }) {
  useMapEvents({ click: onClose });
  return null;
}

function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)}m`;
  return `${(meters / 1000).toFixed(1)}km`;
}

// マーカー上にポップアップカードを表示（React 管理で Leaflet 座標変換を利用）
function SpotPopupCard({
  spot,
  mapRef,
}: {
  spot: Spot;
  mapRef: React.RefObject<LeafletMap | null>;
}) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const update = () => {
      const point = map.latLngToContainerPoint([spot.latitude, spot.longitude]);
      setPos({ x: Math.round(point.x), y: Math.round(point.y) });
    };
    update();
    map.on('move', update);
    map.on('zoom', update);
    return () => {
      map.off('move', update);
      map.off('zoom', update);
    };
  }, [spot, mapRef]);

  if (!pos) return null;

  const CARD_WIDTH = 200;
  const MARGIN = 8;
  const rawLeft = pos.x - CARD_WIDTH / 2;
  const left = Math.max(MARGIN, Math.min(rawLeft, window.innerWidth - CARD_WIDTH - MARGIN));

  return (
    <div
      className="absolute z-[2500] bg-white rounded-lg shadow-lg overflow-hidden"
      style={{
        width: CARD_WIDTH,
        left,
        // iconAnchor y=32 の先端から 8px 上にカード底辺を合わせる
        top: pos.y - 32 - 8,
        transform: 'translateY(-100%)',
      }}
    >
      <div className="p-2">
        {spot.defaultPhotoUrl && (
          <img
            src={spot.defaultPhotoUrl}
            alt={spot.name}
            className="w-full h-24 object-cover rounded mb-2"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
        )}
        <p className="font-bold text-sm leading-snug mb-0.5">{spot.name}</p>
        <p className="text-xs text-gray-500 mb-0.5">
          {getCategoryLabel(spot.category as SpotCategory)} · ★
          {calculateStarScore(spot.rankScore).toFixed(1)}
        </p>
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2 flex-wrap">
          {spot.distance !== undefined && (
            <span className="flex items-center gap-0.5">
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M16.24 7.76l-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12z" />
              </svg>
              {formatDistance(spot.distance)}
            </span>
          )}
          <span className="flex items-center gap-0.5">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
              <ellipse cx="12" cy="17.5" rx="5.5" ry="3" />
              <ellipse cx="5.5" cy="12" rx="2" ry="2.5" />
              <ellipse cx="9.5" cy="9" rx="2" ry="2.5" />
              <ellipse cx="14.5" cy="9" rx="2" ry="2.5" />
              <ellipse cx="18.5" cy="12" rx="2" ry="2.5" />
            </svg>
            {spot.totalCheckins}
          </span>
          <span className="flex items-center gap-0.5">
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            {spot.totalLikes}
          </span>
        </div>
        <Link
          to={`/spots/${spot.id}`}
          className="block w-full text-center text-xs font-medium bg-primary rounded py-2.5 hover:opacity-90 active:opacity-80 !text-white"
        >
          詳細を見る
        </Link>
      </div>
      {/* 下向き矢印（Leaflet Popup 風） */}
      <div
        className="absolute left-1/2 -translate-x-1/2 bottom-0 translate-y-full w-0 h-0"
        style={{
          borderLeft: '8px solid transparent',
          borderRight: '8px solid transparent',
          borderTop: '8px solid white',
        }}
      />
    </div>
  );
}

// 表示範囲が 10km を超えたときに検索境界円を表示
function SearchRadiusCircle() {
  const map = useMap();
  const [center, setCenter] = useState<[number, number]>(() => {
    const c = map.getCenter();
    return [c.lat, c.lng];
  });
  const [visible, setVisible] = useState(false);

  const update = useCallback(() => {
    const c = map.getCenter();
    const visibleRadius = map.distance(c, map.getBounds().getNorthEast());
    setCenter([c.lat, c.lng]);
    setVisible(visibleRadius > MAX_SEARCH_RADIUS);
  }, [map]);

  useMapEvents({ moveend: update, zoomend: update });
  useEffect(() => {
    update();
  }, [update]);

  if (!visible) return null;

  return (
    <Circle
      center={center}
      radius={MAX_SEARCH_RADIUS}
      pathOptions={{
        color: '#3b82f6',
        weight: 1.5,
        fillOpacity: 0.05,
        dashArray: '6 4',
      }}
    />
  );
}

export function MapView() {
  const mapRef = useRef<LeafletMap | null>(null);
  const { latitude, longitude, refresh } = useGeolocationContext();

  // sessionStorage から前回の中心・ズームを復元
  const [savedState] = useState<SavedMapState | null>(loadMapState);

  const initialLat = savedState?.lat ?? latitude ?? DEFAULT_LAT;
  const initialLng = savedState?.lng ?? longitude ?? DEFAULT_LNG;
  const initialZoom = savedState?.zoom ?? DEFAULT_ZOOM;

  const [searchOpen, setSearchOpen] = useState(false);
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null);
  const [actualMapBounds, setActualMapBounds] = useState<MapBounds | null>(null);

  // マップ表示時に位置情報取得を開始（他ページでは不要なため Lazy 起動）
  useEffect(() => {
    refresh();
  }, []); // mount 時の1回のみ。refresh は useCallback で安定している

  // savedState がある場合は GPS による初期移動をスキップ（前回位置を優先）
  const gpsInitialized = useRef(savedState !== null);
  useEffect(() => {
    if (latitude !== null && longitude !== null && !gpsInitialized.current) {
      gpsInitialized.current = true;
      const map = mapRef.current;
      if (map) {
        map.setView([latitude, longitude], map.getZoom());
        // setView が moveend を発火するので setMapBounds は MapEventHandler に委譲
      }
    }
  }, [latitude, longitude]);

  // 現在地ボタン押下後、最新の座標取得完了時にマップを移動する
  const [locating, setLocating] = useState(false);
  const locatingRef = useRef(false);
  const [showOutOfViewHint, setShowOutOfViewHint] = useState(false);
  const outOfViewHintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleDisabledRegisterClick = () => {
    if (outOfViewHintTimerRef.current) clearTimeout(outOfViewHintTimerRef.current);
    setShowOutOfViewHint(true);
    outOfViewHintTimerRef.current = setTimeout(() => setShowOutOfViewHint(false), 2500);
  };
  useEffect(() => {
    if (locatingRef.current && latitude !== null && longitude !== null) {
      locatingRef.current = false;
      setLocating(false);
      const map = mapRef.current;
      if (map) {
        map.setView([latitude, longitude], DEFAULT_ZOOM);
        // setView が moveend を発火するので setMapBounds は MapEventHandler に委譲
      }
    }
  }, [latitude, longitude]);

  const { spots, loading: spotsLoading } = useMapSpots({ bounds: mapBounds });
  const [selectedSpot, setSelectedSpot] = useState<Spot | null>(null);

  const handleLocate = () => {
    clearWeatherCache();
    locatingRef.current = true;
    setLocating(true);
    refresh();
  };

  const handleAddressSelect = useCallback((lat: number, lng: number, zoom: number) => {
    const map = mapRef.current;
    if (map) {
      map.setView([lat, lng], zoom);
    }
  }, []);

  const handleMoveEnd = useCallback((bounds: MapBounds) => {
    setMapBounds(bounds);
  }, []);

  const handleActualBoundsChange = useCallback((bounds: MapBounds) => {
    setActualMapBounds(bounds);
  }, []);

  const handleInitBounds = useCallback((bounds: MapBounds) => {
    setMapBounds(bounds);
  }, []);

  const isLocationInView =
    latitude !== null &&
    longitude !== null &&
    actualMapBounds !== null &&
    latitude >= actualMapBounds.minLat &&
    latitude <= actualMapBounds.maxLat &&
    longitude >= actualMapBounds.minLng &&
    longitude <= actualMapBounds.maxLng;

  // 一覧リンク用パラメータ（bbox を渡してマップと同じ検索範囲にする）
  const centerLat = mapBounds ? (mapBounds.minLat + mapBounds.maxLat) / 2 : initialLat;
  const centerLng = mapBounds ? (mapBounds.minLng + mapBounds.maxLng) / 2 : initialLng;
  const listParams = mapBounds
    ? `lat=${centerLat}&lng=${centerLng}&minLat=${mapBounds.minLat}&maxLat=${mapBounds.maxLat}&minLng=${mapBounds.minLng}&maxLng=${mapBounds.maxLng}`
    : `lat=${centerLat}&lng=${centerLng}`;

  return (
    <div
      className="fixed inset-0"
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 54px)' }}
    >
      <MapContainer
        center={[initialLat, initialLng]}
        zoom={initialZoom}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
        />
        <MapInvalidator />
        <MapInitHandler onInit={handleInitBounds} onActualBoundsChange={handleActualBoundsChange} />
        <MapEventHandler
          onMoveEnd={handleMoveEnd}
          onActualBoundsChange={handleActualBoundsChange}
        />
        <MapClickHandler onClose={() => setSelectedSpot(null)} />
        <SearchRadiusCircle />
        {latitude && longitude && (
          <CurrentLocationMarker latitude={latitude} longitude={longitude} />
        )}
        <MarkerClusterGroup
          chunkedLoading
          maxClusterRadius={60}
          spiderfyOnMaxZoom
          showCoverageOnHover={false}
          zoomToBoundsOnClick
        >
          {spots.map((spot) => (
            <SpotMarker key={spot.id} spot={spot} onSelect={(s) => setSelectedSpot(s)} />
          ))}
        </MarkerClusterGroup>
      </MapContainer>

      {selectedSpot && <SpotPopupCard spot={selectedSpot} mapRef={mapRef} />}

      {/* 右上固定UI: 検索ボックス + 天気バッジ（横並び）
          展開時は left-4 right-4 で全幅、折りたたみ時は right-4 で右端に収納 */}
      <div
        className={`fixed z-[1050] flex flex-row items-center gap-2 ${searchOpen ? 'left-4 right-4' : 'right-4'}`}
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <AddressSearchBox
          open={searchOpen}
          onOpenChange={setSearchOpen}
          onSelect={handleAddressSelect}
        />
        {!searchOpen && <WeatherBadge lat={latitude} lng={longitude} />}
      </div>

      {(locating || spotsLoading) && (
        <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-full px-4 py-2 shadow-md text-sm text-gray-600 z-[1100]">
          {locating ? '現在地を取得中...' : 'スポットを読み込み中...'}
        </div>
      )}

      {/* ボトムナビ上部に固定するオーバーレイ。
          absolute だとアドレスバー表示/非表示で dvh が変動しズレるため fixed を使用。
          z-[1000] で Leaflet コントロール(z≈800)より前面に出す。 */}
      <div
        className="fixed left-0 right-0 flex items-end justify-between px-4 pb-4 z-[1000]"
        style={{ bottom: 'calc(54px + env(safe-area-inset-bottom, 0px) + 8px)' }}
      >
        {/* 左：スポット一覧 */}
        <Link
          to={`/spots?${listParams}`}
          className="bg-white rounded-full px-3 py-1.5 shadow text-sm text-gray-600 active:bg-gray-100 flex items-center gap-1.5"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <line
              x1="8"
              y1="6"
              x2="21"
              y2="6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <line
              x1="8"
              y1="12"
              x2="21"
              y2="12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <line
              x1="8"
              y1="18"
              x2="21"
              y2="18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <rect x="3" y="5" width="2" height="2" rx="0.5" fill="currentColor" />
            <rect x="3" y="11" width="2" height="2" rx="0.5" fill="currentColor" />
            <rect x="3" y="17" width="2" height="2" rx="0.5" fill="currentColor" />
          </svg>
          スポット一覧{spots.length > 0 && `（${spots.length}）`}
        </Link>

        {/* 右：現在地を登録・現在地に戻る（縦並び・間隔広め） */}
        <div className="flex flex-col items-end gap-4">
          {latitude !== null && longitude !== null && (
            <div className="flex flex-col items-end gap-1.5">
              {showOutOfViewHint && (
                <span className="text-xs bg-black/70 text-white rounded-lg px-2.5 py-1.5 whitespace-nowrap">
                  現在地がマップ外です
                </span>
              )}
              {isLocationInView ? (
                <Link
                  to="/spots/new"
                  className="bg-primary text-white rounded-full px-3 py-1.5 shadow-md active:opacity-80 flex items-center gap-1.5 text-sm font-medium"
                >
                  <RegisterSpotIcon />
                  現在地を登録
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={handleDisabledRegisterClick}
                  className="bg-gray-200 text-gray-400 rounded-full px-3 py-1.5 shadow flex items-center gap-1.5 text-sm font-medium"
                >
                  <RegisterSpotIcon />
                  現在地を登録
                </button>
              )}
            </div>
          )}
          <button
            onClick={handleLocate}
            className="bg-white rounded-full shadow-md flex items-center gap-1.5 px-3 py-2 text-primary hover:bg-gray-50 active:bg-gray-100 text-xs font-medium"
            aria-label="現在地に戻る"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 11L22 2L13 21L11 13L3 11Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            現在地
          </button>
        </div>
      </div>
    </div>
  );
}
